import { formatDateTime } from '@shared/lib/format-date';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import type { WalletMonitoringAddress, WalletMonitoringItem } from '@shared/api/contracts';
import {
  displayWalletKind,
  formatWalletAssetBalanceLines,
  formatWalletMoney,
  hasAddressFilters,
  WALLET_KIND_LABEL_KEYS,
  type WalletFilterCatalog,
  type WalletsMonitoringFilters,
} from '@entities/wallets';
import { useWalletAddresses } from '@features/wallets';
import { AddressRow } from './address-row';
import { Button, Skeleton } from '@shared/ui';
import { cn } from '@shared/lib/utils';

interface WalletRowProps {
  wallet: WalletMonitoringItem;
  filters: WalletsMonitoringFilters;
  catalog?: WalletFilterCatalog;
  flattened?: boolean;
  expanded: boolean;
  onToggle: () => void;
  onOpenControlMode: (address: WalletMonitoringAddress) => void;
  onToggleAml: (address: WalletMonitoringAddress) => void;
  isAmlPending: (addressId: number) => boolean;
  isControlModePending: (addressId: number) => boolean;
}

export function WalletRow({
  wallet,
  filters,
  catalog,
  flattened = false,
  expanded,
  onToggle,
  onOpenControlMode,
  onToggleAml,
  isAmlPending,
  isControlModePending,
}: WalletRowProps) {
  const { t } = useTranslation('cabinet');
  const showAddressFilters = hasAddressFilters(filters, {
    networkCodes: catalog?.networkCodes,
    currencyCodes: catalog?.currencyCodes,
  });

  const {
    addresses,
    isLoading,
    error,
    mutate,
  } = useWalletAddresses(wallet.id, expanded, filters, catalog);

  const kind = displayWalletKind(wallet.kind);
  const factAmount = wallet.factBalance.amount;
  const factLabel =
    factAmount == null
      ? t('wallets.balance.noValuation')
      : formatWalletMoney(factAmount, { asset: wallet.factBalance.asset });

  const addressCountLabel = showAddressFilters
    ? t('wallets.wallet.matchingAddresses', {
        matching: wallet.matchingAddressesCount,
        total: wallet.addressesCount,
      })
    : String(wallet.addressesCount);

  const canExpand = wallet.addressesCount > 0;

  const addressesBody = (
    <>
      {isLoading && !addresses.length ? (
        <div className="space-y-2 p-4">
          {Array.from({ length: 2 }).map((_, index) => (
            <Skeleton key={index} className="h-10 w-full rounded-md" />
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-wrap items-center gap-2 p-4">
          <p className="text-sm text-destructive">{t('wallets.wallet.addressesError')}</p>
          <Button variant="outline" size="sm" onClick={() => void mutate()}>
            {t('common.retry', { ns: 'common' })}
          </Button>
        </div>
      ) : !addresses.length ? (
        <div className="p-4 text-sm text-muted-foreground">
          {t('wallets.wallet.noMatchingAddresses')}
        </div>
      ) : (
        <>
          <div className="overflow-x-auto overflow-y-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted/30 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2 text-left font-medium">{t('wallets.table.address')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('wallets.table.network')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('wallets.table.balances')}</th>
                  <th className="w-[1%] whitespace-nowrap px-2 py-2 text-right font-medium">
                    {t('wallets.table.fact')}
                  </th>
                  <th className="px-3 py-2 text-left font-medium">{t('wallets.table.risk')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('wallets.table.lastCheck')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('wallets.table.status')}</th>
                  <th className="px-3 py-2 text-left font-medium">{t('wallets.table.inRotation')}</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {addresses.map((address) => (
                  <AddressRow
                    key={address.id}
                    address={address}
                    onOpenControlMode={() => onOpenControlMode(address)}
                    onToggleAml={() => onToggleAml(address)}
                    isAmlPending={isAmlPending(address.id)}
                    isControlModePending={isControlModePending(address.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  );

  if (flattened) {
    if (!expanded) return null;

    return <div className="border-t border-border/60 bg-muted/10">{addressesBody}</div>;
  }

  return (
    <div className="border-b border-border/60 last:border-0">
      <button
        type="button"
        className={cn(
          'flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-3 py-3 text-left transition-colors duration-fast ease-standard hover:bg-muted/30 sm:px-4',
          !canExpand && 'cursor-default',
        )}
        onClick={() => {
          if (canExpand) onToggle();
        }}
        disabled={!canExpand}
      >
        {canExpand ? (
          <ChevronRight
            className={cn(
              'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
              expanded && 'rotate-90',
            )}
          />
        ) : (
          <span className="inline-block h-3.5 w-3.5 shrink-0" />
        )}
        <span className="min-w-0 flex-1 text-sm font-semibold">{wallet.name}</span>
        <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
          {t(WALLET_KIND_LABEL_KEYS[kind])}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('wallets.wallet.plan')}:{' '}
          {formatWalletMoney(wallet.planBalance.amount, { asset: wallet.planBalance.asset })}
        </span>
        <span
          className="text-xs text-muted-foreground"
          title={wallet.factBalance.observedAt ?? undefined}
        >
          {t('wallets.wallet.fact')}: {factLabel}
          {wallet.factBalance.observedAt
            ? ` · ${formatDateTime(wallet.factBalance.observedAt, 'dd.MM.yyyy HH:mm')}`
            : ''}
        </span>
        <span className="text-xs text-muted-foreground">
          {t('wallets.wallet.addresses')}: {addressCountLabel}
        </span>
        <span className="w-full text-xs text-muted-foreground sm:w-auto">
          {t('wallets.wallet.ledger')}:{' '}
          {formatWalletAssetBalanceLines(
            wallet.assetBalances,
            t('wallets.wallet.historicalNetwork'),
          ).join(', ') || '—'}
        </span>
      </button>

      {!canExpand && (
        <div className="px-4 pb-3 text-sm text-muted-foreground">{t('wallets.wallet.noAddresses')}</div>
      )}

      {expanded && canExpand && (
        <div className="border-t border-border/60 bg-muted/10">{addressesBody}</div>
      )}
    </div>
  );
}
